import { useCallback, useState } from 'react';
import getCredentialLookup from 'services/api/auth/getCredentialLookup';
import decorators from 'utils/decorators';
import ErrorConstants from 'utils/ErrorConstants';

// Delay (ms) before the global interstice loader appears; if the lookup settles
// first the loader never shows (see decorateApiCallWithInterstice). Sourced from
// the shared ErrorConstants default so it tracks the rest of the app.
const INTERSTICE_DELAY_MS = ErrorConstants.INTERSTICE_DEFAULT_TIMEOUT_MS;

// Wrap the OI-4 lookup call so the global interstice loader shows while the
// request is in flight (delayed by INTERSTICE_DELAY_MS to avoid a flash on fast
// responses). withInterstice returns the underlying promise unchanged, so the
// resolved payload / rejection contract of getCredentialLookup is preserved.
const getCredentialLookupWithInterstice = decorators.withInterstice(getCredentialLookup, INTERSTICE_DELAY_MS);

/**
 * Credential-lookup hook (SIM-2 / ACS-4126, SUB-TASK-2a).
 *
 * Exposes { lookup, isLoading, error }. `lookup(credential, credentialType)`
 * POSTs a body of exactly { credential, credentialType } to the OI-4
 * `authenticationMethods` endpoint (via the ufeApi-based service, contract in
 * api-contracts/auth-service-apis.md §1) and, on success, resolves the domain
 * payload unchanged so the screen can branch on it:
 *   - single account: { securityToken, availableAuthMethods, phoneLast4Digits?,
 *     maskedEmail }
 *   - disambiguation:  { requiresEmailConfirmation: true }
 *
 * The service rejects on the backend errors[] / errorCode envelope. This hook
 * catches that rejection and resolves a normalized failure descriptor
 * { error: true, errorCode, errorMessage } so the screen can map the code to AC
 * copy, and sets `error` to the errorCode (or 'server_error' when the backend
 * gave no code, e.g. a bare 5xx). No auto-retry is performed (UL-05).
 *
 * `isLoading` toggles around the in-flight request. While the request is in
 * flight the global interstice loader is shown via decorators.withInterstice;
 * the local `isLoading` flag is retained (UL-02) so the CredentialEntryScreen
 * can disable its own submit button in-flight.
 *
 * SSR-safe: the network call lives inside the callback only. Nothing is logged
 * on any path — the credential value is never written to any console channel
 * (BR-021).
 */
const useCredentialLookup = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const lookup = useCallback(async (credential, credentialType) => {
        setIsLoading(true);
        setError(null);

        try {
            return await getCredentialLookupWithInterstice(credential, credentialType);
        } catch (rejection) {
            // BR-021: never log the credential (or any diagnostic that could
            // carry it). The service rejects with the backend errors[0] shape
            // ({ errorCode, errorMessage }); a bare 5xx rejects without a code.
            const errorCode = rejection?.errorCode || 'server_error';
            const errorMessage = rejection?.errorMessage;
            setError(errorCode);

            return { error: true, errorCode, errorMessage };
        } finally {
            setIsLoading(false);
        }
    }, []);

    return { lookup, isLoading, error };
};

export default useCredentialLookup;
